import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { EndorsementData } from '@ContractBuilder/types';
import { zodResolver } from '@hookform/resolvers/zod';

import type { EndorsementFormState } from '../types';
import { EndorsementStep } from '../types';
import { getInitialValues } from '../utils';
import {
  createEndorsementStep0Schema,
  createEndorsementStep1Schema,
  createEndorsementStep2Schema,
  createEndorsementStep3Schema,
  validationSchema,
} from '../validation/schema';

export function useEndorsementForm(entity?: EndorsementData, isEdit = false, isStandalone = false, currentStep = 0) {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<EndorsementFormState>({
    defaultValues: getInitialValues(entity, isEdit),
    resolver: zodResolver(validationSchema),
    mode: 'onTouched',
  });

  const formValues = watch();

  const isCurrentStepValid = useMemo(() => {
    if (isStandalone && currentStep === EndorsementStep.ParentSelection) {
      return createEndorsementStep0Schema.safeParse(formValues).success;
    }
    if (currentStep === EndorsementStep.SummaryTemplate) {
      return createEndorsementStep1Schema.safeParse(formValues).success;
    }
    if (currentStep === EndorsementStep.BasicInfo) {
      return createEndorsementStep2Schema.safeParse(formValues).success;
    }
    if (currentStep === EndorsementStep.ReasonPremium) {
      return createEndorsementStep3Schema.safeParse(formValues).success;
    }
    return isValid;
  }, [formValues, currentStep, isStandalone, isValid]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'premium_change' && value.premium_change) {
        setValue('additional_premium', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  return {
    control,
    formValues,
    handleSubmit,
    isValid: isCurrentStepValid,
    setValue,
  };
}
